import { z } from 'zod';

/** The control a form edits a field with. */
export const FieldWidget = {
  Text: 'text',
  LongText: 'long',
  Number: 'number',
  Boolean: 'boolean',
  Date: 'date',
  Time: 'time',
  DateTime: 'datetime',
  Select: 'select',
  MultiSelect: 'array',
  Autocomplete: 'autocomplete',
} as const;
export type FieldWidget = (typeof FieldWidget)[keyof typeof FieldWidget];

export interface FieldOption<V = unknown> {
  value: V;
  label: string;
}

/** Each enum's value labels, kept out of Zod's global registry and so out of the API's contract. */
export const enumLabels = z.registry<{ labels: Record<string, string> }, z.ZodEnum>();

/** The copies of a referenced record's name that reads add beside its key, for a field to show. */
export type DisplayCopy =
  | 'typeLabel'
  | 'questionSetTitle'
  | 'hostName'
  | 'roomLabel'
  | 'teacherName';

/** What a field's schema cannot say about it, given per field in an entity's `fields.ts`. */
export interface FieldUi<R> {
  /** Words the field differently from its schema's title. */
  label?: string;
  /** Replaces the widget the field's schema implies. */
  widget?: FieldWidget;
  /**
   * Replaces the choices the schema's enum would give; `[]` where the field offers none or a page
   * loads them.
   */
  options?: readonly FieldOption[];
  /**
   * The key of the record's copy of a referenced record's name, shown in place of the value: a
   * field of the record's own, or a copy its reads add.
   */
  displayField?: Extract<keyof R, string> | DisplayCopy;
}

/** A field as a form takes it. */
export interface FieldDefinition<R> {
  label: string;
  widget: FieldWidget;
  options?: readonly FieldOption[];
  displayField?: Extract<keyof R, string> | DisplayCopy;
}

type ObjectSchema = z.ZodObject<z.ZodRawShape>;

/** The schema a field's value is checked against, past its wrappers and through its pipes. */
export const unwrapField = (schema: z.ZodType): z.ZodType => {
  if (
    schema instanceof z.ZodOptional ||
    schema instanceof z.ZodNullable ||
    schema instanceof z.ZodDefault ||
    schema instanceof z.ZodPrefault ||
    schema instanceof z.ZodReadonly ||
    schema instanceof z.ZodCatch ||
    schema instanceof z.ZodNonOptional ||
    schema instanceof z.ZodLazy
  ) {
    return unwrapField(schema.unwrap() as z.ZodType);
  }
  if (schema instanceof z.ZodPipe) {
    // A transform has no shape of its own, so the value reads as the pipe's input
    return unwrapField(
      (schema.out instanceof z.ZodTransform ? schema.in : schema.out) as z.ZodType
    );
  }
  return schema;
};

/** A field's title, set with `.meta()` as the last call in the field's chain. */
export const getFieldTitle = (schema: z.ZodType): string | undefined => schema.meta()?.title;

/** An enum's values, in its own order, each with the label registered for it. */
export const getEnumOptions = <T extends z.core.util.EnumLike>(
  schema: z.ZodEnum<T>
): FieldOption<T[keyof T]>[] => {
  const labels = new Map(Object.entries(enumLabels.get(schema)?.labels ?? {}));
  return (
    schema.options
      // A numeric enum's options also list its reverse mappings, which the schema refuses
      .filter(value => schema.safeParse(value).success)
      .map(value => {
        const label = labels.get(String(value));
        if (!label) {
          throw new Error(`No label is registered for the enum value "${String(value)}"`);
        }
        return { value, label };
      })
  );
};

const getImpliedWidget = (schema: z.ZodType): FieldWidget => {
  if (schema instanceof z.ZodEnum) {
    return FieldWidget.Select;
  }
  if (schema instanceof z.ZodArray) {
    return FieldWidget.MultiSelect;
  }
  if (schema instanceof z.ZodBoolean) {
    return FieldWidget.Boolean;
  }
  if (schema instanceof z.ZodNumber) {
    return FieldWidget.Number;
  }
  return FieldWidget.Text;
};

const CHOICE_WIDGETS: readonly FieldWidget[] = [
  FieldWidget.Select,
  FieldWidget.MultiSelect,
  FieldWidget.Autocomplete,
];

const getImpliedOptions = (schema: z.ZodType): FieldOption[] | undefined => {
  const choices = schema instanceof z.ZodArray ? unwrapField(schema.element as z.ZodType) : schema;
  return choices instanceof z.ZodEnum ? getEnumOptions(choices) : undefined;
};

const describeField = <R>(property: z.ZodType, key: string, ui: FieldUi<R>): FieldDefinition<R> => {
  const label = ui.label ?? getFieldTitle(property);
  if (!label) {
    throw new Error(`The field "${key}" has no title`);
  }
  const schema = unwrapField(property);
  const widget = ui.widget ?? getImpliedWidget(schema);
  const options =
    ui.options ?? (CHOICE_WIDGETS.includes(widget) ? getImpliedOptions(schema) : undefined);
  return {
    label,
    widget,
    ...(options ? { options } : {}),
    ...(ui.displayField ? { displayField: ui.displayField } : {}),
  };
};

/**
 * The fields forms show from one record schema, each worked out on first read: lazy schemas
 * exist to break import cycles, so nothing here unwraps them while modules load.
 */
export const describeFields = <S extends ObjectSchema, K extends Extract<keyof S['shape'], string>>(
  schema: S,
  fields: { [P in K]: FieldUi<z.output<S>> }
): { readonly [P in K]: Readonly<FieldDefinition<z.output<S>>> } => {
  const definitions = {} as { [P in K]: FieldDefinition<z.output<S>> };
  (Object.keys(fields) as K[]).forEach(key => {
    let definition: FieldDefinition<z.output<S>> | undefined;
    Object.defineProperty(definitions, key, {
      enumerable: true,
      get: () => (definition ??= describeField(schema.shape[key] as z.ZodType, key, fields[key])),
    });
  });
  return definitions;
};
